// French for the standing queue (tab-proposals). Registered on load.
// Queue vocabulary: claim → affirmation (coined — distinct from déclaration/statement and from
// proposition/Proposal, both already claimed by the glossary), unheld → ouvert(e) (mirrors the
// German's own choice of "offen"), hold/held → retenir/retenu(e), disposition → décision,
// disposed → tranché(e), run → exécution (all aligned with the glossary).
import { registerFr } from '../i18n/index.js';

registerFr({
  // ── masthead / page states ─────────────────────────────────────────────────
  'Proposals': 'Propositions',
  'No engagement selected': 'Aucun mandat sélectionné',
  'The review queue is scoped to one engagement — every claim in it names a run, a document and a register it would enter.':
    "La file d’attente de vérification porte sur un seul mandat — chaque affirmation qui s’y trouve nomme une exécution, un document et un registre où elle entrerait.",
  'Every claim the agent has filed in': "Chaque affirmation que l’agent a déposée dans",
  'carries a human disposition — the register is answerable for all of it.':
    'porte une décision humaine — le registre répond de tout.',
  'claims the agent has made in': "affirmations que l’agent a faites dans",
  'that nobody has stood behind. None of it is in the register yet.':
    "que personne n’a encore soutenues. Rien de tout cela n’est encore dans le registre.",
  'Gate': 'Gate',
  'waits on this queue:': "attend sur cette file d’attente :",
  'j/k · a hold · e amend · r refuse · d duplicate · u undo': 'j/k · a retenir · e adapter · r refuser · d doublon · u défaire',
  'Refresh': 'Actualiser',

  // ── the rail's five cells ──────────────────────────────────────────────────
  'UNHELD': 'OUVERTES',
  'COLLIDING': 'EN COLLISION',
  'UNSOURCED': 'SANS PREUVE',
  'FLAGGED': 'SIGNALÉES',
  'SESSION': 'SESSION',
  'claims': 'affirmations',
  'every claim has a disposition': 'chaque affirmation a une décision',
  '{n} run': '{n} exécution',
  '{n} runs': '{n} exécutions',
  'oldest {date}': 'la plus ancienne : {date}',
  'not attributed to a run': 'non attribuée à une exécution',
  'with the record': 'avec le dossier',
  'the comparison did not answer': "la comparaison n’a pas répondu",
  'nothing left to compare': 'plus rien à comparer',
  '≥ {sim} to a register row': '≥ {sim} avec une ligne du registre',
  'no evidence': 'aucune preuve',
  'nothing unheld': "plus rien d’ouvert",
  'judged against the register instead': 'mesurée au registre à la place',
  'lint / low conf': 'lint / conf. faible',
  'lint error or confidence < 0.80': 'erreur de lint ou confiance < 0.80',
  '{done} of {total}': '{done} sur {total}',
  '{n} held': '{n} retenues',
  '{n} refused': '{n} refusées',
  'no queue to work': "aucune file d’attente à traiter",
  'nothing disposed yet': "rien n’est encore tranché",

  // ── zone 1 · the queue ─────────────────────────────────────────────────────
  'The queue': "La file d’attente",
  'reading': 'lecture',
  'nothing to review': 'rien à vérifier',
  '{n} claim unheld': '{n} affirmation ouverte',
  '{n} claims unheld': '{n} affirmations ouvertes',
  '{n} disposed this session': '{n} tranchées dans cette session',
  'The register comparison': 'La comparaison au registre',
  'Register comparison unavailable': 'Comparaison au registre indisponible',
  'Every claim below is unchecked against the record. Holding one still writes; it just writes without the duplicate and contradiction check. The queue does not pretend the check passed.':
    "Chaque affirmation ci-dessous n’est pas vérifiée au regard du dossier. La retenir écrit tout de même ; simplement sans le contrôle de doublon et de contradiction. La file d’attente ne prétend pas que le contrôle a été réussi.",
  'run {id}': "l’exécution {id}",
  'the tool layer pushed back': "la couche d’outils a refusé",
  'times on this run': 'fois sur cette exécution',
  ', and it cited {covered} of the {n} document it was given': ', et elle a cité {covered} document sur {n} qui lui a été fourni',
  ', and it cited {covered} of the {n} documents it was given': ', et elle a cité {covered} documents sur {n} qui lui ont été fournis',
  'Read the evidence closely.': 'Lisez les preuves attentivement.',
  'Scoped to run {run}': "Limité à l’exécution {run}",
  'You arrived here from a link that named one run.': 'Vous êtes arrivé ici via un lien qui désignait une exécution.',
  'Show every run': 'Afficher toutes les exécutions',
  'The review queue could not be read': "La file d’attente de vérification n’a pas pu être lue",
  'reading the queue': "lecture de la file d’attente",
  'Nothing unheld.': "Rien n’est ouvert.",
  'Nothing filed yet.': "Rien n’a encore été déposé.",
  'This engagement has filed': 'Ce mandat a déposé',
  'claims. Every one carries a human disposition:': 'affirmations. Chacune porte une décision humaine :',
  'held,': 'retenues,',
  'amended before holding,': "adaptées avant d’être retenues,",
  'refused. The register is answerable for all of them.': 'refusées. Le registre répond de toutes.',
  'No claim has been filed for this engagement yet. An extraction pass reads the documents in scope and files what it finds here — nothing reaches the register until you hold it.':
    "Aucune affirmation n’a encore été déposée pour ce mandat. Une passe d’extraction lit les documents dans le périmètre et dépose ici ce qu’elle trouve — rien n’atteint le registre tant que vous ne la retenez pas.",
  'asks for': 'demande',
  'and': 'et',
  'This half of it is met.': "Cette moitié en est remplie.",
  'It is not met yet.': "Ce n’est pas encore rempli.",
  'Run an extraction pass →': "Lancer une passe d’extraction →",
  'Open the register': 'Ouvrir le registre',
  'No claim matches this filter': 'Aucune affirmation ne correspond à ce filtre',
  'The queue is scoped to run {run}, and that run filed nothing.': "La file d’attente est limitée à l’exécution {run}, et cette exécution n’a rien déposé.",

  // ── the queue rail (tabs, sort, rows) ──────────────────────────────────────
  'Claims': 'Affirmations',
  'untreated': 'non traité',
  'in thread': 'en fil',
  'nothing held in a thread': 'rien de retenu dans un fil',
  'treated': 'traité',
  'Sort the rail': 'Trier la liste',
  'sort': 'tri',
  'run': 'exécution',
  'type': 'type',
  'weight': 'poids',
  'category': 'catégorie',
  'kind': 'nature',
  'nothing untreated': 'rien de non traité',
  'nothing treated yet': "rien de traité pour l’instant",
  '{left} of {total} unheld': '{left} sur {total} ouvertes',
  'type, weight, category': 'type, poids, catégorie',
  'unattributed': 'non attribué',
  'no type': 'sans type',
  'no weight': 'sans poids',
  'uncategorised': 'sans catégorie',

  // ── kinds, types, weights, statuses ────────────────────────────────────────
  'requirement': 'exigence',
  'revision': 'révision',
  'merge': 'fusion',
  'conflict': 'conflit',
  'deliverable': 'livrable',
  'content item': 'élément de contenu',
  'criterion': 'critère',
  'test case': 'cas de test',
  'question': 'question',
  'functional': 'fonctionnel',
  'non-functional': 'non fonctionnel',
  'non_functional': 'non fonctionnel',
  'constraint': 'contrainte',
  'assumption': 'hypothèse',
  'exclusion': 'exclusion',
  'out_of_scope': 'hors périmètre',
  'must': 'Doit',
  'should': 'Devrait',
  'could': 'Pourrait',
  "won't": 'Exclu',
  'wont': 'Exclu',
  // register/deliverable row statuses (identical to the PortalAdminPage catalogue, so the
  // merged dictionary never disagrees with itself)
  'draft': 'brouillon',
  'pending': 'en attente',
  'proposed': 'proposé',
  'held': 'retenu',
  'confirmed': 'confirmé',
  'descoped': 'retiré du périmètre',
  'baselined': 'dans la baseline',
  'superseded': 'remplacé',
  'planned': 'planifié',
  'in_progress': 'en cours',
  'delivered': 'livré',
  'accepted': 'accepté',
  'dropped': 'abandonné',
  // the page's disposition words
  'amended': 'adapté',
  'refused': 'refusé',
  'amended · held': 'adapté · retenu',
  // run statuses
  'queued': "en file d’attente",
  'starting': 'démarre',
  'running': 'en exécution',
  'done': 'terminé',
  'error': 'échoué',
  'aborted': 'interrompu',
  'timeout': 'délai dépassé',
  'running · will file more': "en exécution · déposera d’autres affirmations",

  // ── flags ──────────────────────────────────────────────────────────────────
  'register comparison unavailable': 'comparaison au registre indisponible',
  'twin': 'jumeau',
  'twin of #{id} in this same run': 'jumeau de #{id} dans cette même exécution',
  'collides': 'en collision',
  'collides with {ref}': 'entre en collision avec {ref}',
  'lint': 'Lint',
  'lint error': 'erreur de lint',
  'no ev': 'sans preuve',
  'warn': 'avertissement',
  '{n} lint warning': '{n} avertissement de lint',
  '{n} lint warnings': '{n} avertissements de lint',
  'low conf': 'conf. faible',
  'agent confidence {n}': "confiance de l’agent {n}",
  'near': 'proche',
  'nearest {ref} at {sim}': 'plus proche : {ref}, à {sim}',

  // ── the stand ──────────────────────────────────────────────────────────────
  'filed by': 'déposé par',
  'no run': 'aucune exécution',
  ' on ': ' le ',
  '{i} of {total}': '{i} sur {total}',
  'The two things the record says': 'Les deux versions que porte le dossier',
  'the later document is {ref}, {date}': 'le document le plus récent est {ref}, {date}',
  'Position A': 'Position A',
  'Position B': 'Position B',
  'this side wins': "ce côté l’emporte",
  'let this side win': "laisser ce côté l’emporter",
  'Hinges on {x}.': 'Dépend de {x}.',
  'The anchor was matched automatically — check it before resolving, because the resolution rewrites whatever it points at.':
    "L’ancre a été associée automatiquement — vérifiez-la avant de résoudre, car la résolution réécrit ce qu’elle désigne.",
  'Deliverable': 'Livrable',
  'Definition of done — the sentence acceptance is judged against': "Critères d’acceptation — la phrase à laquelle l’acceptation est mesurée",
  '{n} requirement would be linked to it.': '{n} exigence y serait liée.',
  '{n} requirements would be linked to it.': '{n} exigences y seraient liées.',
  'Acceptance criterion for': "Critère d’acceptation pour",
  'Measurement method': 'Méthode de mesure',
  'Test case': 'Cas de test',
  'Preconditions:': 'Conditions préalables :',
  'Expected result': 'Résultat attendu',
  'The question that would go on the client-call agenda': "La question qui figurerait à l’ordre du jour de l’entretien client",
  'The revision that would enter {target}': 'La révision qui entrerait dans {target}',
  'the register': 'le registre',
  'the previous revision is kept, never overwritten': 'la révision précédente est conservée, jamais écrasée',
  'type {from} → {to}': 'type {from} → {to}',
  'priority {from} → {to}': 'priorité {from} → {to}',
  'now {status}': 'désormais {status}',
  'The statement that would enter the register': 'La déclaration qui entrerait dans le registre',
  'Linter': 'Linter',
  'no blocking error': 'aucune erreur bloquante',
  'one error blocks a testable criterion': 'une erreur bloque un critère testable',
  'warning': 'avertissement',
  'pass': 'réussi',
  'Observable subject, observable outcome, a threshold a tester can check.':
    "Un sujet observable, un résultat observable, un seuil qu’un testeur peut vérifier.",
  'What the record already says': 'Ce que dit déjà le dossier',
  'unavailable': 'indisponible',
  'the row this resolution rewrites': 'la ligne que cette résolution réécrit',
  'the requirement this criterion hangs from': "l’exigence dont dépend ce critère",
  'not re-run for a disposed claim': 'non réexécutée pour une affirmation tranchée',
  'trigram over the deliverable names': 'trigramme sur les noms de livrables',
  'trigram over the register · pg_trgm': 'trigramme sur le registre · pg_trgm',
  'nothing to compare it against': 'rien à quoi la comparer',
  'register comparison': 'comparaison au registre',
  'did not answer': "n’a pas répondu",
  'This claim has': "Cette affirmation n’a",
  'NOT': 'PAS',
  'been checked against the record. The page says so rather than showing an empty result that reads like a clean bill.':
    "été vérifiée au regard du dossier. La page le précise plutôt que d’afficher un résultat vide qui donnerait l’impression d’un bilan propre.",
  '{n} criterion already': 'déjà {n} critère',
  '{n} criteria already': 'déjà {n} critères',
  'no criteria yet': "aucun critère pour l’instant",
  'A criterion is measured against its own requirement and its sibling criteria — never against the register at large, where the parent always scores highest and tells you nothing.':
    "Un critère est mesuré par rapport à sa propre exigence et à ses critères frères — jamais par rapport à l’ensemble du registre, où le parent obtient toujours le meilleur score sans rien révéler.",
  'Open {ref} →': 'Ouvrir {ref} →',
  'anchor matched automatically': 'ancre associée automatiquement',
  'anchored by the operator': "ancrée par l’opérateur",
  'what {ref} would say once position {side} wins': 'ce que {ref} dirait une fois que la position {side} l’emporte',
  'filed by the same run': 'déposé par la même exécution',
  ', {s} s later': ', {s} s plus tard',
  ', {s} s earlier': ', {s} s plus tôt',
  'similarity': 'similarité',
  'Refuse — the run said it twice': "Refuser — l’exécution l’a dit deux fois",
  'Stand on #{id} →': 'Passer à #{id} →',
  'Two claims one run filed minutes apart can both be held, and were: a register reference was created and deleted fifteen minutes later — burned for good, because references are never reused.':
    "Deux affirmations déposées par une même exécution à quelques minutes d’écart peuvent toutes deux être retenues — et l’ont été : une référence de registre a été créée puis supprimée quinze minutes plus tard, brûlée pour de bon, car les références ne sont jamais réutilisées.",
  '— under the 0.60 line': '— sous la limite de 0.60',
  'The names all but match. What differs is the sentence acceptance is judged against:':
    "Les noms se ressemblent presque totalement. Ce qui diffère, c’est la phrase à laquelle l’acceptation est mesurée :",
  'Refuse — duplicate of {ref}': 'Refuser — doublon de {ref}',
  'Refuse — contradicts {ref}': 'Refuser — contredit {ref}',
  'next closest': 'le plus proche suivant',
  '— nothing near': '— rien à proximité',
  'Nothing in the register scores above': 'Rien dans le registre ne dépasse',
  '— this claim is new ground. The trigram catches lexical near-twins and misses semantic ones, so this is a clean filter, not a clean bill.':
    '— cette affirmation est un terrain inédit. Le trigramme repère les quasi-jumeaux lexicaux et laisse passer les jumeaux sémantiques ; c’est donc un filtre propre, pas un bilan propre.',
  'Disposed — the register comparison runs for standing claims only, and is not re-run here.':
    "Tranché — la comparaison au registre ne s’exécute que pour les affirmations ouvertes, et n’est pas relancée ici.",
  'Evidence': 'Preuves',
  '{n} span': '{n} extrait',
  '{n} spans': '{n} extraits',
  'view span': "voir l’extrait",
  'conf {n}': 'conf. {n}',
  'None attached —': 'Aucune jointe —',
  'a deliverable is derived from the requirements it claims to satisfy, not from a document. Judge it against the register above.':
    "un livrable découle des exigences qu’il prétend satisfaire, pas d’un document. Mesurez-le par rapport au registre ci-dessus.",
  'a criterion is derived from its requirement, not from a document. It inherits {ref}’s evidence.':
    "un critère découle de son exigence, pas d’un document. Il hérite des preuves de {ref}.",
  'its parent': 'son exigence parente',
  'this claim rests on nothing you can check. That alone is a reason to refuse it.':
    "cette affirmation ne repose sur rien de vérifiable. C’est à elle seule une raison de la refuser.",
  'Why the agent proposed it': "Pourquoi l’agent l’a proposée",
  'Reviewer note': 'Note du reviewer',
  'Refused': 'Refusé',
  'Held': 'Retenu',
  'no register row was written. Reason recorded:': 'aucune ligne de registre n’a été écrite. Motif consigné :',
  'written to the register as': 'écrite dans le registre comme',
  '· amended before holding': "· adaptée avant d’être retenue",
  'Undo': 'Défaire',
  'Walk the refusal back — the claim stands unheld again': "Annuler le refus — l’affirmation redevient ouverte",
  'Revert the hold — deletes the row it created and puts the claim back in the queue':
    "Annuler la retenue — supprime la ligne créée et remet l’affirmation dans la file d’attente",
  'Un-refuse — back to the queue': "Annuler le refus — retour à la file d’attente",
  'Un-hold — back to the queue': "Annuler la retenue — retour à la file d’attente",
  'Holding this writes': 'Retenir ceci écrit',
  '. Nothing else moves.': ". Rien d’autre ne bouge.",

  // ── what the hold writes ───────────────────────────────────────────────────
  'a draft requirement, its revision 1 and {links}': 'une exigence en brouillon, sa révision 1 et {links}',
  '{n} evidence link': '{n} lien de preuve',
  '{n} evidence links': '{n} liens de preuve',
  'no evidence link': 'aucun lien de preuve',
  'a deliverable and {links}': 'un livrable et {links}',
  '{n} requirement link': "{n} lien d’exigence",
  '{n} requirement links': "{n} liens d’exigence",
  'an acceptance criterion under {ref}': "un critère d’acceptation sous {ref}",
  'its requirement': 'son exigence',
  'a test case and {links}': 'un cas de test et {links}',
  '{n} criterion link': '{n} lien de critère',
  '{n} criterion links': '{n} liens de critère',
  'a new revision of {ref} — the previous one is kept, never overwritten': 'une nouvelle révision de {ref} — la précédente est conservée, jamais écrasée',
  'the anchored requirement': "l’exigence ancrée",
  'a new revision of {ref} carrying the winning side': 'une nouvelle révision de {ref} portant la position gagnante',
  'a draft requirement carrying the winning side': 'une exigence en brouillon portant la position gagnante',
  'an open question on the client-call agenda': "une question ouverte à l’ordre du jour de l’entretien client",
  'a register row': 'une ligne de registre',

  // ── the action foot and its panels ─────────────────────────────────────────
  'Hold — position {side} wins': "Retenir — la position {side} l’emporte",
  'Hold it — pick a side': 'Retenir — choisir une position',
  'Hold it': 'Retenir',
  'No side wins yet: both positions land as an open question on the Register page — your client-call agenda':
    "Aucune position ne l’emporte encore : les deux atterrissent comme question ouverte sur la page Registre — votre ordre du jour d’entretien client",
  'Log it as an open question': 'Consigner comme question ouverte',
  'Amend, then hold': 'Adapter, puis retenir',
  'Hold exactly as proposed AND put it on the client-call agenda as an open question to refine':
    "Retenir exactement tel que proposé ET l’ajouter à l’ordre du jour de l’entretien client comme question ouverte à affiner",
  'Hold · and put to the client': 'Retenir · et soumettre au client',
  'Refuse': 'Refuser',
  'Remove this machine proposal outright — no refusal record, the audit entry keeps its payload. A claim worth an answer is refused instead.':
    'Supprimer entièrement cette proposition automatique — aucune trace de refus, la piste d’audit conserve les données. Une affirmation qui mérite une réponse est refusée à la place.',
  'Delete…': 'Supprimer…',
  'Position {side} wins. It is applied as a NEW REVISION of the anchored requirement — revisions are append-only, so the losing position stays preserved on this proposal.':
    "La position {side} l’emporte. Elle est appliquée comme NOUVELLE RÉVISION de l’exigence ancrée — les révisions sont uniquement ajoutées, la position perdante reste donc conservée sur cette proposition.",
  '— none fits: create a NEW requirement from the winning position': '— aucune ne convient : créer une NOUVELLE exigence à partir de la position gagnante',
  'statement override (optional) — leave empty to take the winning side verbatim':
    'déclaration de remplacement (facultatif) — laisser vide pour reprendre la position gagnante mot pour mot',
  'why you resolved it this way (recorded on the proposal)': "pourquoi vous l’avez résolu ainsi (consigné sur la proposition)",
  'Amend, then hold — the amendment is recorded as': "Adapter, puis retenir — l’adaptation est consignée comme",
  'held with edits': 'retenue avec modifications',
  ', and the distance between the two is what the run report learns from': ", et c’est de l’écart entre les deux que le rapport d’exécution tire ses enseignements",
  'name': 'nom',
  'description': 'description',
  'title': 'titre',
  'expected result': 'résultat attendu',
  'preconditions': 'conditions préalables',
  'test data reference': 'référence des données de test',
  'context': 'contexte',
  'measurement method': 'méthode de mesure',
  'rationale': 'justification',
  'why you changed it (recorded on the proposal)': "pourquoi vous l’avez modifié (consigné sur la proposition)",
  'Hold the amended text': 'Retenir le texte adapté',
  'Cancel': 'Annuler',
  'Refused because — the reason is the only signal the run report ever gets back':
    "Refusé parce que — le motif est le seul signal que le rapport d’exécution reçoive jamais en retour",
  'Duplicate of {ref}': 'Doublon de {ref}',
  'Contradicts {ref}': 'Contredit {ref}',
  'note (optional) — recorded on the proposal beside the reason': 'note (facultative) — consignée sur la proposition à côté du motif',

  // ── refusal reasons (labels in src/client/api/proposals.ts) ────────────────
  'Duplicate': 'Doublon',
  'Contradicts the register': 'Contredit le registre',
  'Superseded by later evidence': 'Remplacé par une preuve plus récente',
  'Not testable': 'Non testable',
  'Not a requirement': "N’est pas une exigence",
  'Evidence does not support it': "La preuve ne l’étaye pas",
  'Out of scope': 'Hors périmètre',
  'Other': 'Autre',

  // ── stand empty states ─────────────────────────────────────────────────────
  'Nothing treated yet.': "Rien de traité pour l’instant.",
  'Every claim on file carries a human disposition.': 'Chaque affirmation au dossier porte une décision humaine.',
  'The {n} treated claim is on the other tab.': "La {n} affirmation traitée se trouve sur l’autre onglet.",
  'The {n} treated claims are on the other tab.': "Les {n} affirmations traitées se trouvent sur l’autre onglet.",
  'Hold or refuse a claim and it moves here, with its disposition and its undo.':
    "Retenez ou refusez une affirmation, elle atterrit ici avec sa décision et la possibilité de l’annuler.",
  'Show the {n} treated →': 'Afficher les {n} traitées →',

  // ── zone 2 · this session ──────────────────────────────────────────────────
  'This session': 'Cette session',
  '{n} · newest first': '{n} · les plus récentes d’abord',
  'Nothing disposed yet. Each disposition lands here with an undo, so a wrong hold is one click away for as long as this session lasts — not a filter change and a scan of':
    "Rien n’est encore tranché. Chaque décision atterrit ici avec la possibilité de l’annuler, une retenue erronée n’est donc jamais qu’à un clic tant que dure cette session — pas besoin de changer de filtre et de parcourir",
  'held rows.': 'les lignes retenues.',

  // ── zone 3 · the runs ──────────────────────────────────────────────────────
  'The runs behind this queue': "Les exécutions derrière cette file d’attente",
  '{n} run with unheld claims': '{n} exécution avec des affirmations ouvertes',
  '{n} runs with unheld claims': '{n} exécutions avec des affirmations ouvertes',
  'unheld of': 'ouvertes sur',
  'filed ·': 'déposées ·',
  'cited {covered} of the {n} document it was given': 'a cité {covered} document sur {n} qui lui a été fourni',
  'cited {covered} of the {n} documents it was given': 'a cité {covered} documents sur {n} qui lui ont été fournis',
  'cited {n} document': 'a cité {n} document',
  'cited {n} documents': 'a cité {n} documents',
  'read {n} register item': 'a lu {n} élément du registre',
  'read {n} register items': 'a lu {n} éléments du registre',
  'no close discipline recorded': 'aucune discipline de clôture consignée',
  '{n} tool rejection': "{n} rejet d’outil",
  '{n} tool rejections': "{n} rejets d’outil",
  'read the evidence closely': 'lisez les preuves attentivement',
  'clean tool log': "journal d’outils propre",
  '{n} turn': '{n} tour',
  '{n} turns': '{n} tours',
  'hide the account': 'masquer le rapport',
  'what it says it did': "ce qu’elle dit avoir fait",
  'the collision check did not answer — a bulk hold cannot be offered blind':
    "le contrôle de collision n’a pas répondu — une retenue groupée ne peut pas être proposée à l’aveugle",
  'Hold all — check unavailable': 'Tout retenir — contrôle indisponible',
  'a bulk control must not be able to commit a known collision': 'un contrôle groupé ne doit pas pouvoir valider une collision connue',
  'Hold all — {n} collide': 'Tout retenir — {n} en collision',
  'Hold all {n}': 'Tout retenir : {n}',
  'This run wrote no closing account. The close discipline is enforced at the tool layer':
    "Cette exécution n’a rédigé aucun rapport de clôture. La discipline de clôture est imposée au niveau de la couche d’outils",
  'a run without one either predates it or ended before it could satisfy it.':
    "une exécution qui n’en a pas lui est soit antérieure, soit s’est terminée avant de pouvoir s’y conformer.",

  // ── the span sheet ─────────────────────────────────────────────────────────
  'the span in context': "l’extrait en contexte",
  'Close': 'Fermer',
  'The document could not be read': "Le document n’a pas pu être lu",
  'chars {start}–{end}': 'car. {start}–{end}',
  'The quoted span is validated as a literal substring of {ref} at propose time and again on accept — an agent cannot file a quotation this document does not contain.':
    "L’extrait cité est validé comme sous-chaîne littérale de {ref} au moment de la proposition, puis à nouveau lors de l’acceptation — un agent ne peut pas déposer une citation que ce document ne contient pas.",

  // ── the two modals ─────────────────────────────────────────────────────────
  'Hold {n} claim': 'Retenir {n} affirmation',
  'Hold {n} claims': 'Retenir {n} affirmations',
  'Hold them all': 'Tout retenir',
  'Every unheld claim in run #{run} will be held exactly as proposed, one at a time. Anything the register refuses (a baselined requirement, a failed evidence check) stays unheld and is reported back.':
    "Chaque affirmation ouverte de l’exécution #{run} sera retenue exactement telle que proposée, une à la fois. Tout ce que le registre refuse (une exigence dans la baseline, un contrôle de preuve échoué) reste ouvert et est signalé en retour.",
  'Delete claim #{id}': "Supprimer l’affirmation #{id}",
  'Deleting…': 'Suppression…',
  'Delete permanently': 'Supprimer définitivement',
  'This removes the machine’s claim outright — it never reaches the register and it will NOT appear as refused with a reason, so the next pass cannot learn from it. The audit log keeps its payload. A claim worth an answer is refused, not deleted.':
    "Ceci supprime entièrement l’affirmation de la machine — elle n’atteint jamais le registre et n’apparaîtra PAS comme refusée avec un motif ; la prochaine exécution ne peut donc rien en apprendre. La piste d’audit conserve les données. Une affirmation qui mérite une réponse est refusée, pas supprimée.",

  // ── toasts ─────────────────────────────────────────────────────────────────
  '#{id} held': '#{id} retenu',
  '{ref} on the client-call agenda': "{ref} à l’ordre du jour de l’entretien client",
  'The hold failed — nothing was written.': "La retenue a échoué — rien n’a été écrit.",
  '#{id} refused — {reason}': '#{id} refusé — {reason}',
  'The refusal failed — nothing was written.': "Le refus a échoué — rien n’a été écrit.",
  'The undo was refused.': "L’annulation a été refusée.",
  '{n} claim held': '{n} affirmation retenue',
  '{n} claims held': '{n} affirmations retenues',
  '{n} refused and still unheld': '{n} refusées et toujours ouvertes',
  '#{id} deleted — the audit log keeps its payload': '#{id} supprimé — la piste d’audit conserve les données',
  'The delete was refused.': 'La suppression a été refusée.',
  'Nothing collides with this claim — d only fires when the page is showing you what it duplicates.':
    "Rien n’entre en collision avec cette affirmation — d ne s’active que lorsque la page vous montre ce qu’elle duplique.",
  'Amends {ref}':
    'Modifie {ref}',
  'This amendment names no field to change — refuse it.':
    'Cette modification ne nomme aucun champ à changer – refusez-la.',
  'Why:':
    'Pourquoi :',
  'Only the fields listed above change. Everything else on {ref} stays exactly as it is.':
    'Seuls les champs listés ci-dessus changent. Tout le reste de {ref} demeure inchangé.',

  'deliverable amendment': 'Modification du livrable',
  'content item amendment': 'Modification de l’élément de contenu',
  'criterion amendment': 'Modification du critère',
  'test case amendment': 'Modification du cas de test',
  'provisional mark': 'Marque provisoire',

  'the row this amendment changes':
    'la ligne que cette modification change',
  'The record could not be read — hold this only after checking {ref} yourself.':
    'Le contenu n’a pas pu être lu – ne retenez ceci qu’après avoir vérifié {ref} vous-même.',
  '{ref} no longer exists — it was deleted after this was filed. Accepting will refuse; refuse it here instead.':
    '{ref} n’existe plus – il a été supprimé après le dépôt. L’acceptation sera refusée ; refusez-le plutôt ici.',
  'Read live from the register just now — not a copy taken when the proposal was filed.':
    'Lu en direct dans le registre à l’instant – ce n’est pas une copie datant du dépôt.',

  'now': 'actuellement',
  'becomes': 'devient',
  'Unchanged — the same set, re-sent.':
    'Inchangé – le même ensemble, renvoyé.',
  'Unchanged — the same text, re-sent.':
    'Inchangé – le même texte, renvoyé.',
  'nothing': 'rien',
  'Only those fields change. Everything else on {ref} stays exactly as it is.':
    'Seuls ces champs changent. Tout le reste de {ref} demeure inchangé.',

  'Held as proposed — a list is not edited here.':
    'Retenu tel que proposé – une liste ne se modifie pas ici.',

}, 'ProposalsPage');
